import type { HTMLAttributes, ReactElement } from "react";
import { cloneElement, isValidElement } from "react";
import { cn } from "../../../utils/cn";

export interface FormFieldProps extends HTMLAttributes<HTMLDivElement> {
  label?: string;
  htmlFor?: string;
  required?: boolean;
  help?: string;
  error?: string;
}

export function FormField({
  label,
  htmlFor,
  required,
  help,
  error,
  className,
  children,
  ...props
}: FormFieldProps) {
  const describedBy =
    [help ? `${htmlFor}-help` : null, error ? `${htmlFor}-error` : null]
      .filter(Boolean)
      .join(" ") || undefined;
  const enhancedChild = isValidElement(children)
    ? (cloneElement(children as ReactElement<any>, {
        "aria-describedby":
          [children.props?.["aria-describedby"], describedBy].filter(Boolean).join(" ") ||
          undefined,
        "aria-invalid": error ? true : children.props?.["aria-invalid"],
        id: htmlFor || children.props?.id,
      }) as typeof children)
    : children;
  return (
    <div className={cn("space-y-1", className)} {...props}>
      {label && (
        <label htmlFor={htmlFor} className="block text-sm font-medium">
          {label}
          {required && <span className="ml-1 text-danger">*</span>}
        </label>
      )}
      {enhancedChild}
      {help && (
        <div id={`${htmlFor}-help`} className="text-xs text-muted">
          {help}
        </div>
      )}
      {error && (
        <div id={`${htmlFor}-error`} className="text-xs text-danger">
          {error}
        </div>
      )}
    </div>
  );
}

export default FormField;
